// The worm (K.worm): cumulative runs by over, one series.
import { esc, type ScoreInput } from './types';

export interface OverEnd { over: number; x: number; runs: number; wickets: number; fell: number }

/**
 * The score at the last ball of each over, in order. The label is the scorer's over.ball ("13.2" is the second
 * ball after 13 overs), so x is the fractional over: whole at the end of an over, fractional when the block ends mid-over.
 */
export function overEnds(input: ScoreInput): OverEnd[] {
  const ends = new Map<number, { x: number; runs: number; wickets: number }>();
  for (const b of input.balls) {
    const [o, n] = b.label.split('.').map(Number);
    ends.set(o, { x: o + n / 6, runs: b.runs, wickets: b.wickets });
  }
  let wk = input.start.wickets;
  return [...ends].map(([over, e]) => { const fell = e.wickets - wk; wk = e.wickets; return { over, ...e, fell }; });
}

/** The worm's points in overs and runs, starting from the start score. */
export const wormPoints = (input: ScoreInput): [number, number][] =>
  [[input.startOver, input.start.runs] as [number, number]].concat(overEnds(input).map(e => [e.x, e.runs]));

export function drawWorm(input: ScoreInput): string {
  const W = 640, H = 300, m = { l: 40, r: 14, t: 30, b: 30 }, N = input.overs, s0 = input.startOver;
  const ends = overEnds(input), max = Math.max(input.start.runs, ...ends.map(e => e.runs));
  const top = Math.max(40, Math.ceil((max + 10) / 40) * 40);
  const X = (v: number) => m.l + ((W - m.l - m.r) * (v - s0)) / N, Y = (v: number) => H - m.b - ((H - m.t - m.b) * v) / top;
  let s = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(input.label)}">`;
  for (let v = 0; v <= top; v += 40) s += `<line x1="${m.l}" x2="${W - m.r}" y1="${Y(v)}" y2="${Y(v)}" stroke="#fff" stroke-opacity=".07"/><text x="${m.l - 8}" y="${Y(v) + 4}" text-anchor="end" font-size="10.5" fill="#6a9a6a">${v}</text>`;
  const step = N <= 10 ? 1 : 5;
  for (let v = s0; v <= s0 + N; v += step) s += `<text x="${X(v)}" y="${H - m.b + 18}" text-anchor="middle" font-size="10.5" fill="#6a9a6a">${v}</text>`;
  const pts = wormPoints(input).map(([x, y]) => [X(x), Y(y)]);
  s += `<polyline points="${pts.map(p => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ')}" fill="none" stroke="${input.color}" stroke-width="2.6" stroke-linejoin="round" stroke-linecap="round"/>`;
  ends.forEach(e => { for (let k = 0; k < e.fell; k++) s += `<circle class="wicket" cx="${X(e.x).toFixed(1)}" cy="${(Y(e.runs) - k * 8).toFixed(1)}" r="3.8" fill="#0d1a0d" stroke="${input.color}" stroke-width="2"/>`; });
  return s + '</svg>';
}
